import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect, useMemo, useRef } from 'react'
import { Box } from '@mui/material'
import { createLegalDraftExtensions } from '../../extensions/legalDraftEditor'
import { plainTextToTiptapJSON } from '../../utils/tiptapPlainText'

export interface LegalDraftTiptapEditorProps {
  value: string
  onChange: (next: string) => void
  suggestionPhrases: string[]
  onAcceptPhrase: (phrase: string) => void
  onDismissPhrase: (phrase: string) => void
  fontFamily: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  textAlign: 'left' | 'center' | 'right' | 'justify'
}

/**
 * Editor văn bản tố tụng (TipTap / ProseMirror): đồng bộ plain text \\n, gợi ý vàng, Tab/Esc.
 */
export default function LegalDraftTiptapEditor({
  value,
  onChange,
  suggestionPhrases,
  onAcceptPhrase,
  onDismissPhrase,
  fontFamily,
  fontSize,
  bold,
  italic,
  underline,
  textAlign,
}: LegalDraftTiptapEditorProps) {
  const optsRef = useRef({
    getPhrases: () => suggestionPhrases as string[],
    onAcceptPhrase,
    onDismissPhrase,
  })
  optsRef.current.getPhrases = () => suggestionPhrases
  optsRef.current.onAcceptPhrase = onAcceptPhrase
  optsRef.current.onDismissPhrase = onDismissPhrase

  const extensions = useMemo(
    () =>
      createLegalDraftExtensions({
        getPhrases: () => optsRef.current.getPhrases(),
        onAcceptPhrase: (p) => optsRef.current.onAcceptPhrase(p),
        onDismissPhrase: (p) => optsRef.current.onDismissPhrase(p),
      }),
    [],
  )

  const editor = useEditor(
    {
      extensions,
      content: plainTextToTiptapJSON(value),
      editorProps: {
        attributes: {
          class: 'tiptap legal-draft-editor',
          spellcheck: 'false',
        },
      },
      onUpdate: ({ editor: ed }) => {
        onChange(ed.getText({ blockSeparator: '\n' }))
      },
    },
    [extensions],
  )

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    const cur = editor.getText({ blockSeparator: '\n' })
    if (cur === value) return
    editor.commands.setContent(plainTextToTiptapJSON(value), { emitUpdate: false })
  }, [value, editor])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.view.dispatch(editor.state.tr.setMeta('refreshSuggestionDecorations', true))
  }, [suggestionPhrases, editor])

  if (!editor) {
    return (
      <Box
        sx={{
          fontFamily: fontFamily + ', serif',
          fontSize,
          minHeight: 400,
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      />
    )
  }

  return (
    <Box
      sx={{
        minHeight: 400,
        outline: 'none',
        '& .tiptap.legal-draft-editor': {
          fontFamily: `${fontFamily}, serif`,
          fontSize,
          lineHeight: 1.8,
          fontWeight: bold ? 700 : 400,
          fontStyle: italic ? 'italic' : 'normal',
          textDecoration: underline ? 'underline' : 'none',
          textAlign,
          outline: 'none',
          minHeight: 400,
          whiteSpace: 'pre-wrap',
          tabSize: 4,
          MozTabSize: 4,
        },
        '& .tiptap.legal-draft-editor p': {
          margin: 0,
        },
        '& .tiptap.legal-draft-editor:focus': {
          outline: 'none',
        },
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  )
}
